import { JOB_STEP_ORDER, jobSteps, currentStep } from '../cgOrders/jobStages';
import type { JobStageInput, JobStepKey } from '../cgOrders/jobStages';

/**
 * The factory's own pipeline board.
 *
 * The portal had no board. It had three headed lists — Needs you, With TAP,
 * Completed — which answer "is it my turn" and nothing else. A factory looking
 * at four live jobs could not see that two were waiting on TAP's approval and
 * two were mid-production, because both read as "With TAP" and "Needs you".
 *
 * ── Why the nine steps and not the eight statuses ──────────────────────────
 *
 * Because the nine steps are what El Salvador actually named, and the whole
 * point of jobStages.ts is that both sides count the same things. A board
 * built on cg_orders.status would put "Sampling" on one column and leave the
 * factory to work out whether that means make them or wait for a verdict —
 * which is the ambiguity the nine steps exist to remove.
 *
 * ── Every column, always ───────────────────────────────────────────────────
 *
 * Columns are returned even when empty. A board that hides its empty columns
 * changes shape as work moves through it, so the same job appears in a
 * different place on the screen from one day to the next and nobody builds a
 * memory of where to look. Kyu asked for all the stages visible at a glance;
 * that means all of them, all the time.
 */

export interface JobBoardCard<T> {
  order: T;
  orderId: string;
  orderNo: string;
  /** Whose move it is at this step: the factory's, or TAP's. */
  turn: 'supplier' | 'tap' | null;
}

export interface JobBoardColumn<T> {
  key: JobStepKey;
  label: string;
  cards: JobBoardCard<T>[];
}

/**
 * What a board card needs, beyond the stage fields.
 *
 * Structural rather than a named row type, for the same reason JobStageInput
 * is three fields: this is called with the supplier list payload, and
 * demanding a full order row would force a cast at the one call site.
 */
export type BoardOrder = JobStageInput & { order_id: string; order_no: string };

/**
 * Sort every job into the step it is sitting on.
 *
 * A finished job has no current step — jobSteps deliberately returns none, so
 * that a bar does not imply somebody still owes something. On a board it still
 * has to be somewhere, and the honest column is the last one it reached:
 * Delivered for a delivered job, Shipped for one that has shipped.
 *
 * `sampleSubmitted` is not available on the list payload — only the single-job
 * RPC counts photos — so the board reads the status alone. The consequence is
 * narrow and worth stating: a job whose photos have been uploaded but whose
 * status has not yet moved shows under "Making samples" rather than "Samples
 * submitted". The status is the shared source of truth between the two sides,
 * so erring toward it keeps the two boards agreeing.
 */
export function jobBoard<T extends BoardOrder>(orders: T[]): JobBoardColumn<T>[] {
  const columns = new Map<JobStepKey, JobBoardCard<T>[]>();
  for (const key of JOB_STEP_ORDER) {
    columns.set(key, []);
  }

  // Non-empty by construction, so the fallback below is a value rather than a
  // `?? undefined` the type system has to be argued with.
  const FIRST: JobStepKey = 'po_received';

  for (const order of orders) {
    const steps = jobSteps(order, { sampleSubmitted: false });
    const current = currentStep(steps);
    const doneOf = (step: JobStepKey) =>
      steps.find((entry) => entry.key === step)?.state === 'done';
    const lastDone = [...JOB_STEP_ORDER].reverse().find(doneOf);
    // Finished jobs have no current step, so they take the last one they
    // completed. A job that has completed nothing takes the first column.
    //
    // ── Why the last two columns are filed differently ──────────────────
    //
    // Kyu: "this test order is still under Shipped, I have not marked it
    // delivered from our end yet, but in Supplier's view it's already
    // delivered." The card even said so — "Day 1 in Shipped", sitting in the
    // Delivered column.
    //
    // The cause is that seven of these nine column headings name an ACTIVITY
    // and two name a STATE. Filing by current step — the first thing not yet
    // done — reads correctly for the seven: a job whose next undone step is
    // "Making samples" is indeed being sampled. It inverts for the two, where
    // the next undone step is the thing that has NOT happened. A shipped
    // order's next undone step is delivery, so it landed under "Delivered",
    // which every reader takes to mean it arrived.
    //
    // It bit one step earlier too, unreported: a job in production with the
    // ready flag raised has "Shipped" as its next undone step, so it sat in
    // the Shipped column while still being built.
    //
    // So the two state columns are filed by what is DONE, and the activity
    // columns keep filing by what is current. Delivered means delivered;
    // Shipped means shipped; and a job waiting on either sits in the last
    // place it actually reached.
    const key: JobStepKey = doneOf('delivered')
      ? 'delivered'
      : doneOf('shipped')
        ? 'shipped'
        : current !== null && current.key !== 'shipped' && current.key !== 'delivered'
          ? current.key
          : (lastDone ?? FIRST);

    columns.get(key)?.push({
      order,
      orderId: order.order_id,
      orderNo: order.order_no,
      turn: current?.turn ?? null,
    });
  }

  return JOB_STEP_ORDER.map((key) => ({
    key,
    label: STEP_LABELS[key],
    cards: columns.get(key) ?? [],
  }));
}

/**
 * The column headings.
 *
 * Deliberately the same nine words the stage bar uses inside a job. A board
 * that says "Acknowledgement" over a bar that says "PO acknowledged" is two
 * vocabularies for one thing, and the factory has to learn both.
 */
const STEP_LABELS: Record<JobStepKey, string> = {
  po_received: 'PO received',
  po_acknowledged: 'PO acknowledged',
  making_samples: 'Making samples',
  samples_submitted: 'Samples submitted',
  awaiting_approval: 'Awaiting TAP approval',
  samples_approved: 'Samples approved',
  in_production: 'In production',
  shipped: 'Shipped',
  delivered: 'Delivered',
};

/** How many jobs are waiting on the factory right now. Drives the tab badge. */
export function jobsNeedingYou<T extends BoardOrder>(orders: T[]): number {
  return orders.filter((order) => {
    const current = currentStep(jobSteps(order, { sampleSubmitted: false }));
    return current?.turn === 'supplier';
  }).length;
}
